<div class="qc-drawer-root" *ngIf="open">
  <div
    class="qc-scrim"
    role="presentation"
    (click)="emitClose()"
    aria-hidden="true"
  ></div>

  <div
    class="qc-panel"
    role="dialog"
    aria-modal="true"
    aria-labelledby="qc-drawer-title"
    (click)="$event.stopPropagation()"
  >
    <header class="qc-header">
      <h2 id="qc-drawer-title" class="qc-title">Quick compare</h2>
      <button type="button" class="qc-close btn" (click)="emitClose()" aria-label="Close">
        Close
      </button>
    </header>

    <div class="qc-columns">
      <ng-container *ngIf="bothColumnsCompleted; else qcStandardColumns">
        <div class="qc-paired-outer">
          <div class="qc-paired-headers">
            <section class="qc-col qc-col-head-only" [attr.aria-label]="primaryDomainLabel || 'Your site'">
              <h3 class="qc-col-label">{{ primaryDomainLabel || 'Your site' }}</h3>
              <a class="qc-url qc-url-link" [href]="primaryUrl" target="_blank" rel="noopener noreferrer">{{
                primaryUrl
              }}</a>
            </section>
            <section class="qc-col qc-col-head-only" [attr.aria-label]="compareDomainLabel || 'Compare to'">
              <div class="qc-col-head-row">
                <h3 class="qc-col-label">{{ compareDomainLabel || 'Compare to' }}</h3>
                <button
                  type="button"
                  class="btn qc-force-refetch-btn"
                  (click)="forceRefetchCompare()"
                  [disabled]="!canForceRefetchCompare"
                  title="Delete stored analysis and re-crawl this competitor (OnPage + Labs ranked keywords)"
                >
                  Re-fetch analysis
                </button>
              </div>
              <a class="qc-url qc-url-link" [href]="compareUrl" target="_blank" rel="noopener noreferrer">{{
                compareUrl
              }}</a>
            </section>
          </div>
          <div class="qc-paired-sync">
            <div class="qc-sync-cell">
              <p class="qc-metric-title">{{ pageTitle(primaryColumn.analysis) }}</p>
            </div>
            <div class="qc-sync-cell">
              <p class="qc-metric-title">{{ pageTitle(compareColumn.analysis) }}</p>
            </div>
            <div class="qc-sync-cell">
              <dl class="qc-dl">
                <dt>On-page score</dt>
                <dd>{{ primaryColumn.analysis.onpageScore ?? '—' }}</dd>
                <dt>Word count</dt>
                <dd>{{ primaryColumn.analysis.wordCount ?? '—' }}</dd>
                <dt>Est. traffic / mo</dt>
                <dd>{{ formatEstTraffic(primaryColumn.analysis) }}</dd>
                <dt>Ranked keywords (in DB)</dt>
                <dd>
                  {{ rankedKeywordDisplayCount(primaryColumn.analysis) }}
                  <ng-container
                    *ngTemplateOutlet="rankedKeywordsNotes; context: { analysis: primaryColumn.analysis }"
                  ></ng-container>
                </dd>
              </dl>
            </div>
            <div class="qc-sync-cell">
              <dl class="qc-dl">
                <dt>On-page score</dt>
                <dd>{{ compareColumn.analysis.onpageScore ?? '—' }}</dd>
                <dt>Word count</dt>
                <dd>{{ compareColumn.analysis.wordCount ?? '—' }}</dd>
                <dt>Est. traffic / mo</dt>
                <dd>{{ formatEstTraffic(compareColumn.analysis) }}</dd>
                <dt>Ranked keywords (in DB)</dt>
                <dd>
                  {{ rankedKeywordDisplayCount(compareColumn.analysis) }}
                  <ng-container
                    *ngTemplateOutlet="rankedKeywordsNotes; context: { analysis: compareColumn.analysis }"
                  ></ng-container>
                </dd>
              </dl>
            </div>
            <div class="qc-sync-cell">
              <ng-container *ngTemplateOutlet="qcTopicFitBlock; context: { side: 'primary' }"></ng-container>
            </div>
            <div class="qc-sync-cell">
              <ng-container *ngTemplateOutlet="qcTopicFitBlock; context: { side: 'compare' }"></ng-container>
            </div>
            <!-- Outlinks get their own row of the paired grid, above incoming:
                 the grid pairs primary/compare cell by cell, so the two link
                 questions must not share a cell or the rows stop lining up. -->
            <div class="qc-sync-cell">
              <ng-container *ngTemplateOutlet="qcOutgoingBlock; context: { side: 'primary' }"></ng-container>
            </div>
            <div class="qc-sync-cell">
              <ng-container *ngTemplateOutlet="qcOutgoingBlock; context: { side: 'compare' }"></ng-container>
            </div>
            <div class="qc-sync-cell">
              <ng-container *ngTemplateOutlet="qcIncomingBlock; context: { side: 'primary' }"></ng-container>
            </div>
            <div class="qc-sync-cell">
              <ng-container *ngTemplateOutlet="qcIncomingBlock; context: { side: 'compare' }"></ng-container>
            </div>
            <div class="qc-sync-cell">
              <ng-container *ngTemplateOutlet="qcFreshnessBlock; context: { side: 'primary' }"></ng-container>
            </div>
            <div class="qc-sync-cell">
              <ng-container *ngTemplateOutlet="qcFreshnessBlock; context: { side: 'compare' }"></ng-container>
            </div>
            <div class="qc-sync-cell">
              <ng-container *ngTemplateOutlet="qcIssuesBlock; context: { side: 'primary' }"></ng-container>
            </div>
            <div class="qc-sync-cell">
              <ng-container *ngTemplateOutlet="qcIssuesBlock; context: { side: 'compare' }"></ng-container>
            </div>
          </div>
        </div>
      </ng-container>

      <ng-template #qcStandardColumns>
        <section class="qc-col" [attr.aria-label]="primaryDomainLabel || 'Your site'">
          <h3 class="qc-col-label">{{ primaryDomainLabel || 'Your site' }}</h3>
          <a class="qc-url qc-url-link" [href]="primaryUrl" target="_blank" rel="noopener noreferrer">{{
            primaryUrl
          }}</a>

          <div class="qc-state" *ngIf="primaryColumn.status === 'loading'">
            <div class="loading-spinner loading-spinner-sm"></div>
            <span>Loading analysis…</span>
          </div>

          <div class="qc-state qc-analyzing" *ngIf="primaryColumn.status === 'analyzing'">
            <div class="loading-spinner loading-spinner-sm"></div>
            <span>Analysis in progress…</span>
            <p class="qc-progress" *ngIf="primaryColumn.progress">
              Crawled {{ primaryColumn.progress.pagesCrawled }} · Queue {{ primaryColumn.progress.pagesInQueue }}
            </p>
          </div>

          <div class="qc-state qc-error" *ngIf="primaryColumn.status === 'error'">
            <p>{{ primaryColumn.error }}</p>
          </div>

          <div class="qc-state qc-empty" *ngIf="primaryColumn.status === 'empty'">
            <p>No analysis yet for this URL.</p>
          </div>

          <!-- Opening this drawer spends nothing. Both situations below stop
               here and wait to be asked, and they say which one they are: a
               page we have never fetched costs a crawl, a page we hold but
               have no keywords for costs only the Labs call. -->
          <div class="qc-state qc-prompt" *ngIf="primaryColumn.status === 'prompt'">
            <p class="qc-prompt-msg" *ngIf="promptReasonFor('primary') === 'not_analyzed'">
              This URL hasn't been analyzed yet — fetching it crawls the page (~$0.01–0.05).
            </p>
            <p class="qc-prompt-msg" *ngIf="promptReasonFor('primary') === 'no_keywords'">
              We hold this page, but no ranked keywords for it yet — fetching them uses Labs (~$0.015).
            </p>
            <button type="button" class="btn qc-prompt-btn" (click)="confirmAnalysis('primary')">
              Fetch now
            </button>
          </div>

          <div class="qc-metrics" *ngIf="primaryColumn.status === 'completed' && primaryColumn.analysis">
            <p class="qc-metric-title">{{ pageTitle(primaryColumn.analysis) }}</p>
            <dl class="qc-dl">
              <dt>On-page score</dt>
              <dd>{{ primaryColumn.analysis.onpageScore ?? '—' }}</dd>
              <dt>Word count</dt>
              <dd>{{ primaryColumn.analysis.wordCount ?? '—' }}</dd>
              <dt>Est. traffic / mo</dt>
              <dd>{{ formatEstTraffic(primaryColumn.analysis) }}</dd>
              <dt>Ranked keywords (in DB)</dt>
              <dd>
                {{ rankedKeywordDisplayCount(primaryColumn.analysis) }}
                <ng-container
                  *ngTemplateOutlet="rankedKeywordsNotes; context: { analysis: primaryColumn.analysis }"
                ></ng-container>
              </dd>
            </dl>

            <ng-container *ngTemplateOutlet="qcTopicFitBlock; context: { side: 'primary' }"></ng-container>

            <ng-container *ngTemplateOutlet="qcOutgoingBlock; context: { side: 'primary' }"></ng-container>
            <ng-container *ngTemplateOutlet="qcIncomingBlock; context: { side: 'primary' }"></ng-container>

            <ng-container *ngTemplateOutlet="qcFreshnessBlock; context: { side: 'primary' }"></ng-container>
            <ng-container *ngTemplateOutlet="qcIssuesBlock; context: { side: 'primary' }"></ng-container>
          </div>
        </section>

        <section class="qc-col" [attr.aria-label]="compareDomainLabel || 'Compare to'">
          <div class="qc-col-head-row">
            <h3 class="qc-col-label">{{ compareDomainLabel || 'Compare to' }}</h3>
            <button
              type="button"
              class="btn qc-force-refetch-btn"
              (click)="forceRefetchCompare()"
              [disabled]="!canForceRefetchCompare"
              title="Delete stored analysis and re-crawl this competitor (OnPage + Labs ranked keywords)"
            >
              {{ compareColumn.status === 'analyzing' ? 'Re-fetching…' : 'Re-fetch analysis' }}
            </button>
          </div>
          <a class="qc-url qc-url-link" [href]="compareUrl" target="_blank" rel="noopener noreferrer">{{
            compareUrl
          }}</a>

          <div class="qc-state" *ngIf="compareColumn.status === 'loading'">
            <div class="loading-spinner loading-spinner-sm"></div>
            <span>Loading analysis…</span>
          </div>

          <div class="qc-state qc-analyzing" *ngIf="compareColumn.status === 'analyzing'">
            <div class="loading-spinner loading-spinner-sm"></div>
            <span>Analysis in progress…</span>
            <p class="qc-progress" *ngIf="compareColumn.progress">
              Crawled {{ compareColumn.progress.pagesCrawled }} · Queue {{ compareColumn.progress.pagesInQueue }}
            </p>
          </div>

          <div class="qc-state qc-error" *ngIf="compareColumn.status === 'error'">
            <p>{{ compareColumn.error }}</p>
          </div>

          <div class="qc-state qc-empty" *ngIf="compareColumn.status === 'empty'">
            <p>No analysis yet for this URL.</p>
          </div>

          <!-- Opening this drawer spends nothing. Both situations below stop
               here and wait to be asked, and they say which one they are: a
               page we have never fetched costs a crawl, a page we hold but
               have no keywords for costs only the Labs call. -->
          <div class="qc-state qc-prompt" *ngIf="compareColumn.status === 'prompt'">
            <p class="qc-prompt-msg" *ngIf="promptReasonFor('compare') === 'not_analyzed'">
              This URL hasn't been analyzed yet — fetching it crawls the page (~$0.01–0.05).
            </p>
            <p class="qc-prompt-msg" *ngIf="promptReasonFor('compare') === 'no_keywords'">
              We hold this page, but no ranked keywords for it yet — fetching them uses Labs (~$0.015).
            </p>
            <button type="button" class="btn qc-prompt-btn" (click)="confirmAnalysis('compare')">
              Fetch now
            </button>
          </div>

          <div class="qc-metrics" *ngIf="compareColumn.status === 'completed' && compareColumn.analysis">
            <p class="qc-metric-title">{{ pageTitle(compareColumn.analysis) }}</p>
            <dl class="qc-dl">
              <dt>On-page score</dt>
              <dd>{{ compareColumn.analysis.onpageScore ?? '—' }}</dd>
              <dt>Word count</dt>
              <dd>{{ compareColumn.analysis.wordCount ?? '—' }}</dd>
              <dt>Est. traffic / mo</dt>
              <dd>{{ formatEstTraffic(compareColumn.analysis) }}</dd>
              <dt>Ranked keywords (in DB)</dt>
              <dd>
                {{ rankedKeywordDisplayCount(compareColumn.analysis) }}
                <ng-container
                  *ngTemplateOutlet="rankedKeywordsNotes; context: { analysis: compareColumn.analysis }"
                ></ng-container>
              </dd>
            </dl>

            <ng-container *ngTemplateOutlet="qcTopicFitBlock; context: { side: 'compare' }"></ng-container>

            <ng-container *ngTemplateOutlet="qcOutgoingBlock; context: { side: 'compare' }"></ng-container>
            <ng-container *ngTemplateOutlet="qcIncomingBlock; context: { side: 'compare' }"></ng-container>

            <ng-container *ngTemplateOutlet="qcFreshnessBlock; context: { side: 'compare' }"></ng-container>
            <ng-container *ngTemplateOutlet="qcIssuesBlock; context: { side: 'compare' }"></ng-container>
          </div>
        </section>
      </ng-template>

      <!-- Full-width: keyword overlap, opportunities, competitive diagnosis (both sides loaded) -->
      <div class="qc-span-full" *ngIf="bothColumnsCompleted">
        <p class="qc-section-intro">{{ keywordPartialHint }}</p>

        <div class="qc-metric-card card card--pad qc-overlap-card">
          <div class="metric-label">Keyword overlap and opportunities</div>
          <p class="qc-metric-note" *ngIf="showKeywordDependencyCallout">
            {{
              rankedKeywordsNoteForAnalysis(primaryColumn.analysis) ||
                rankedKeywordsNoteForAnalysis(compareColumn.analysis)
            }}
          </p>
          <ng-container *ngIf="isDevView()">
            <p
              class="qc-metric-note qc-metric-note-dev"
              *ngIf="rankedKeywordsDevNoteForAnalysis(primaryColumn.analysis) as primaryDevNote"
            >
              Your page · {{ primaryDevNote }}
            </p>
            <p
              class="qc-metric-note qc-metric-note-dev"
              *ngIf="rankedKeywordsDevNoteForAnalysis(compareColumn.analysis) as compareDevNote"
            >
              Compare page · {{ compareDevNote }}
            </p>
          </ng-container>

          <ng-container *ngIf="computeKeywordOverlap() as overlap; else qcOverlapMissing">
            <div class="qc-overlap-summary">
              <div class="qc-overlap-stat qc-overlap-shared">
                <div class="qc-overlap-label">Shared keywords</div>
                <div class="qc-overlap-value">{{ overlap.sharedCount }}</div>
                <div class="qc-overlap-detail" *ngIf="overlap.sharedCount > 0">
                  Your avg pos: <strong>{{ overlap.sharedAvgPositionYour }}</strong> · Compare avg pos:
                  <strong>{{ overlap.sharedAvgPositionCompetitor }}</strong>
                </div>
              </div>
              <div class="qc-overlap-stat qc-overlap-yours">
                <div class="qc-overlap-label">Your unique</div>
                <div class="qc-overlap-value">{{ overlap.yourUniqueCount }}</div>
              </div>
              <div class="qc-overlap-stat qc-overlap-theirs">
                <div class="qc-overlap-label">Their unique</div>
                <div class="qc-overlap-value">{{ overlap.competitorUniqueCount }}</div>
              </div>
            </div>

            <div class="qc-opportunities" *ngIf="getKeywordOpportunities().length > 0">
              <button type="button" class="qc-dropdown-toggle qc-opportunities-toggle" (click)="toggleKeywordOpportunities()">
                <span class="qc-opportunities-icon">&#128161;</span>
                <span>{{ getKeywordOpportunities().length }} Content opportunities</span>
                <span class="qc-dropdown-arrow" [class.expanded]="isKeywordOpportunitiesExpanded()">&#9660;</span>
              </button>
              <div class="qc-opportunities-content" *ngIf="isKeywordOpportunitiesExpanded()">
                <p class="qc-metric-note">Competitor-only keywords with meaningful volume. {{ keywordPartialHint }}</p>
                <ul class="qc-opportunities-list">
                  <li *ngFor="let opp of getKeywordOpportunities()">
                    <span class="qc-opportunity-kw">{{ opp.keyword }}</span>
                    <span class="qc-opportunity-meta">Pos {{ opp.position }} · {{ opp.searchVolume | number }} vol</span>
                  </li>
                </ul>
              </div>
            </div>

            <div class="qc-keyword-details" *ngIf="overlap.sharedCount > 0 || overlap.yourUniqueCount > 0 || overlap.competitorUniqueCount > 0">
              <button type="button" class="qc-dropdown-toggle qc-opportunities-toggle" (click)="toggleKeywordDetails()">
                <span>Show exact ranking keywords and positions</span>
                <span class="qc-dropdown-arrow" [class.expanded]="isKeywordDetailsExpanded()">&#9660;</span>
              </button>
              <div class="qc-opportunities-content" *ngIf="isKeywordDetailsExpanded()">
                <div class="qc-keyword-details-grid">
                  <div class="qc-keyword-column">
                    <h4 class="qc-keyword-col-title">Shared</h4>
                    <ul class="qc-keyword-list" *ngIf="getSharedKeywordsWithPositions().length > 0; else qcNoShared">
                      <li *ngFor="let kw of getSharedKeywordsWithPositions()">
                        <span class="qc-opportunity-kw">{{ kw.keyword }}</span>
                        <span class="qc-opportunity-meta">You {{ kw.yourPosition }} · Them {{ kw.competitorPosition }}</span>
                      </li>
                    </ul>
                    <ng-template #qcNoShared>
                      <p class="qc-muted">No shared keywords in loaded dataset.</p>
                    </ng-template>
                  </div>

                  <div class="qc-keyword-column">
                    <h4 class="qc-keyword-col-title">Your unique</h4>
                    <ul class="qc-keyword-list" *ngIf="getPrimaryUniqueKeywords().length > 0; else qcNoPrimaryUnique">
                      <li *ngFor="let kw of getPrimaryUniqueKeywords()">
                        <span class="qc-opportunity-kw">{{ kw.keyword }}</span>
                        <span class="qc-opportunity-meta">Pos {{ kw.position }}</span>
                      </li>
                    </ul>
                    <ng-template #qcNoPrimaryUnique>
                      <p class="qc-muted">No unique keywords on your side in loaded dataset.</p>
                    </ng-template>
                  </div>

                  <div class="qc-keyword-column">
                    <h4 class="qc-keyword-col-title">Their unique</h4>
                    <ul class="qc-keyword-list" *ngIf="getCompareUniqueKeywords().length > 0; else qcNoCompareUnique">
                      <li *ngFor="let kw of getCompareUniqueKeywords()">
                        <span class="qc-opportunity-kw">{{ kw.keyword }}</span>
                        <span class="qc-opportunity-meta">Pos {{ kw.position }}</span>
                      </li>
                    </ul>
                    <ng-template #qcNoCompareUnique>
                      <p class="qc-muted">No unique keywords on compare side in loaded dataset.</p>
                    </ng-template>
                  </div>
                </div>
              </div>
            </div>
          </ng-container>
          <ng-template #qcOverlapMissing>
            <p class="qc-muted">Ranked keyword data is not in the database for one or both URLs yet. {{ keywordFetchHint }}</p>
          </ng-template>
        </div>

        <div
          class="qc-diagnosis-banner"
          [class.qc-diagnosis-competitor]="hasCompetitorAdvantagesInDiagnosis() && !hasYourAdvantagesInDiagnosis()"
          [class.qc-diagnosis-yours]="hasYourAdvantagesInDiagnosis() && !hasCompetitorAdvantagesInDiagnosis()"
          [class.qc-diagnosis-mixed]="hasCompetitorAdvantagesInDiagnosis() && hasYourAdvantagesInDiagnosis()"
        >
          <div class="qc-diagnosis-header" *ngIf="getDiagnosisBullets().length > 0">
            <span class="qc-diagnosis-icon" *ngIf="hasCompetitorAdvantagesInDiagnosis() && !hasYourAdvantagesInDiagnosis()">&#9888;&#65039;</span>
            <span class="qc-diagnosis-icon" *ngIf="hasYourAdvantagesInDiagnosis() && !hasCompetitorAdvantagesInDiagnosis()">&#10004;&#65039;</span>
            <span class="qc-diagnosis-icon" *ngIf="hasCompetitorAdvantagesInDiagnosis() && hasYourAdvantagesInDiagnosis()">&#128202;</span>
            <h3 class="qc-diagnosis-title" *ngIf="hasCompetitorAdvantagesInDiagnosis() && !hasYourAdvantagesInDiagnosis()">Why they may have an edge</h3>
            <h3 class="qc-diagnosis-title" *ngIf="hasYourAdvantagesInDiagnosis() && !hasCompetitorAdvantagesInDiagnosis()">Your competitive advantages</h3>
            <h3 class="qc-diagnosis-title" *ngIf="hasCompetitorAdvantagesInDiagnosis() && hasYourAdvantagesInDiagnosis()">Competitive snapshot</h3>
          </div>
          <h3 class="qc-diagnosis-title qc-diagnosis-title-only" *ngIf="getDiagnosisBullets().length === 0">Competitive advantages</h3>
          <ul class="qc-diagnosis-list" *ngIf="getDiagnosisBullets().length > 0">
            <li
              *ngFor="let b of getDiagnosisBullets()"
              [class.qc-bullet-competitor]="b.winner === 'competitor'"
              [class.qc-bullet-you]="b.winner === 'you'"
            >
              <span class="qc-bullet-icon">{{ b.winner === 'competitor' ? '&#8722;' : '+' }}</span>
              <span>{{ b.text }}</span>
            </li>
          </ul>
          <p class="qc-muted qc-diagnosis-empty" *ngIf="getDiagnosisBullets().length === 0">
            No strong advantages detected with the data loaded here. Run Deep Compare for fuller keyword and SERP context.
          </p>
          <p class="qc-metric-note" *ngIf="showKeywordDependencyCallout || getDiagnosisBullets().length === 0">
            {{ keywordPartialHint }}
          </p>
        </div>

        <!-- Supporting pages by topic -->
        <div class="qc-metric-card card card--pad qc-supporting-pages-card" *ngIf="supportingPages.status !== 'idle'">
          <button type="button" class="qc-dropdown-toggle qc-supporting-pages-toggle" (click)="toggleSupportingPages()">
            <span class="metric-label">Supporting pages by topic</span>
            <span class="qc-supporting-pages-status" *ngIf="supportingPages.status === 'loading'">
              <span class="qc-loading-spinner-small"></span> Loading…
            </span>
            <span class="qc-supporting-pages-status" *ngIf="supportingPages.status === 'ready'">
              {{ supportingPages.topics.length }} topic{{ supportingPages.topics.length !== 1 ? 's' : '' }}
            </span>
            <span class="qc-dropdown-arrow" [class.expanded]="supportingPages.expanded">&#9660;</span>
          </button>

          <div class="qc-supporting-pages-content" *ngIf="supportingPages.expanded && supportingPages.status === 'ready'">
            <p class="qc-metric-note" *ngIf="supportingPages.topics.length === 0">
              No matching supporting pages found for the top topics on these pages.
            </p>
            <div class="qc-supporting-topic" *ngFor="let topic of supportingPages.topics">
              <div class="qc-supporting-topic-header">
                <span class="badge badge--pill">{{ topic.topicLabel }}</span>
              </div>
              <div class="qc-supporting-cols">
                <div class="qc-supporting-col">
                  <div class="qc-supporting-col-label">{{ primaryDomainLabel || 'Your site' }}</div>
                  <div class="qc-supporting-page-item" *ngFor="let page of topic.yourPages">
                    <span class="qc-supporting-page-path" [title]="page.url">{{ formatSupportingUrl(page.url) }}</span>
                    <span class="qc-supporting-page-score">{{ page.seoIntentScore | number:'1.2-2' }}</span>
                    <span class="qc-supporting-page-traffic" *ngIf="formatSupportingTraffic(page.estimatedOrganicTraffic)">
                      {{ formatSupportingTraffic(page.estimatedOrganicTraffic) }}
                    </span>
                  </div>
                  <div class="qc-supporting-empty" *ngIf="topic.yourPages.length === 0">No pages found</div>
                </div>
                <div class="qc-supporting-col">
                  <div class="qc-supporting-col-label">{{ compareDomainLabel || 'Compare to' }}</div>
                  <div class="qc-supporting-page-item" *ngFor="let page of topic.theirPages">
                    <span class="qc-supporting-page-path" [title]="page.url">{{ formatSupportingUrl(page.url) }}</span>
                    <span class="qc-supporting-page-score">{{ page.seoIntentScore | number:'1.2-2' }}</span>
                    <span class="qc-supporting-page-traffic" *ngIf="formatSupportingTraffic(page.estimatedOrganicTraffic)">
                      {{ formatSupportingTraffic(page.estimatedOrganicTraffic) }}
                    </span>
                  </div>
                  <div class="qc-supporting-empty" *ngIf="topic.theirPages.length === 0">No pages found</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- What they cover that your page doesn't (section coverage) -->
        <div class="qc-metric-card card card--pad qc-section-gap-card" *ngIf="sectionGap.status !== 'idle'">
          <button type="button" class="qc-dropdown-toggle qc-section-gap-toggle" (click)="toggleSectionGap()">
            <span class="metric-label">What they cover that your page doesn't</span>
            <span class="qc-supporting-pages-status" *ngIf="sectionGap.status === 'loading'">
              <span class="qc-loading-spinner-small"></span> Loading…
            </span>
            <span
              class="qc-section-gap-count"
              *ngIf="sectionGap.status === 'ready'"
              [class.has-gaps]="sectionGapRows().length > 0 || sectionGapRankDrivenGaps().length > 0"
            >{{ sectionGapStatusLabel() }}</span>
            <span class="qc-section-gap-count" *ngIf="sectionGap.status === 'unavailable' || sectionGap.status === 'error'">—</span>
            <span class="qc-dropdown-arrow" [class.expanded]="sectionGap.expanded">&#9660;</span>
          </button>

          <div class="qc-section-gap-content" *ngIf="sectionGap.expanded">
            <p class="qc-metric-note" *ngIf="sectionGap.status === 'unavailable'">
              Section coverage needs the content-gap service running (<code>npm run content-gap:serve</code>).
            </p>
            <p class="qc-metric-note" *ngIf="sectionGap.status === 'error'">{{ sectionGap.error }}</p>
            <p class="qc-metric-note" *ngIf="sectionGap.skippedNote">{{ sectionGap.skippedNote }}</p>
            <p class="qc-metric-note qc-section-gap-warn" *ngIf="sectionGap.warningNote">{{ sectionGap.warningNote }}</p>

            <ng-container *ngIf="sectionGap.status === 'ready'">
              <p class="qc-section-gap-intro">
                {{ compareDomainLabel || 'Their page' }}'s sections vs your whole page — compared against this page only, not the whole domain.
              </p>
              <div class="qc-section-gap-verdict" *ngIf="sectionGapRows().length === 0 && !sectionGap.skippedNote">
                <p
                  class="qc-metric-note"
                  [class.qc-section-gap-good]="sectionGapVerdict().outlook === 'no-gaps'"
                >{{ sectionGapVerdict().headline }}</p>
                <p class="qc-metric-note qc-section-gap-next">{{ sectionGapVerdict().nextStep }}</p>
              </div>
              <div class="qc-section-gap-rank-driven" *ngIf="sectionGapRankDrivenGaps().length > 0">
                <p class="qc-section-gap-rank-driven-lead">Rank-driven gaps — on-page passages</p>
                <div
                  class="qc-section-gap-rank-driven-block"
                  *ngFor="let gap of sectionGapRankDrivenGaps()"
                >
                  <div class="qc-section-gap-rank-driven-row">
                    <span class="qc-section-gap-rank-driven-badge badge badge--warning">Rank-driven</span>
                    <span class="qc-section-gap-rank-driven-text">{{ gap.evidenceLine }}</span>
                  </div>
                  <div class="qc-rank-driven-passage" *ngIf="gap.rivalPassageHeading || gap.rivalPassage">
                    <span class="qc-rank-driven-passage-label">Their page</span>
                    <div class="qc-rank-driven-passage-heading-row" *ngIf="gap.rivalPassageHeading">
                      <span
                        class="badge qc-rank-driven-heading-tag"
                        *ngIf="rankDrivenHeadingTag(gap.rivalPassageHeadingLevel, gap.rivalPassageKind)"
                      >
                        {{ rankDrivenHeadingTag(gap.rivalPassageHeadingLevel, gap.rivalPassageKind) }}
                      </span>
                      <p class="qc-rank-driven-passage-heading">
                        <ng-container
                          *ngFor="
                            let seg of sectionGapRankDrivenPassageSegments(
                              gap.keyword,
                              gap.rivalPassageHeading
                            )
                          "
                        >
                          <mark *ngIf="seg.highlight">{{ seg.text }}</mark>
                          <ng-container *ngIf="!seg.highlight">{{ seg.text }}</ng-container>
                        </ng-container>
                      </p>
                    </div>
                    <p class="qc-rank-driven-passage-text" *ngIf="gap.rivalPassage">
                      <ng-container
                        *ngFor="
                          let seg of sectionGapRankDrivenPassageSegments(gap.keyword, gap.rivalPassage)
                        "
                      >
                        <mark *ngIf="seg.highlight">{{ seg.text }}</mark>
                        <ng-container *ngIf="!seg.highlight">{{ seg.text }}</ng-container>
                      </ng-container>
                    </p>
                  </div>
                  <div class="qc-rank-driven-passage qc-rank-driven-passage--yours">
                    <span class="qc-rank-driven-passage-label">Your page</span>
                    <div class="qc-rank-driven-passage-heading-row" *ngIf="gap.focalPassageHeading">
                      <span
                        class="badge qc-rank-driven-heading-tag"
                        *ngIf="rankDrivenHeadingTag(gap.focalPassageHeadingLevel, gap.focalPassageKind)"
                      >
                        {{ rankDrivenHeadingTag(gap.focalPassageHeadingLevel, gap.focalPassageKind) }}
                      </span>
                      <p class="qc-rank-driven-passage-heading">
                        <ng-container
                          *ngFor="
                            let seg of sectionGapRankDrivenPassageSegments(
                              gap.keyword,
                              gap.focalPassageHeading
                            )
                          "
                        >
                          <mark *ngIf="seg.highlight">{{ seg.text }}</mark>
                          <ng-container *ngIf="!seg.highlight">{{ seg.text }}</ng-container>
                        </ng-container>
                      </p>
                    </div>
                    <p class="qc-rank-driven-passage-text" *ngIf="gap.focalPassage">
                      <ng-container
                        *ngFor="
                          let seg of sectionGapRankDrivenPassageSegments(gap.keyword, gap.focalPassage)
                        "
                      >
                        <mark *ngIf="seg.highlight">{{ seg.text }}</mark>
                        <ng-container *ngIf="!seg.highlight">{{ seg.text }}</ng-container>
                      </ng-container>
                    </p>
                    <p
                      class="qc-rank-driven-passage-missing"
                      *ngIf="!gap.focalPassage && !gap.focalPassageHeading"
                    >
                      No matching passage for this keyword.
                    </p>
                  </div>
                  <p
                    class="qc-rank-driven-mismatch"
                    *ngIf="gap.queryFamilyMismatch && gap.mismatchNote"
                  >
                    {{ gap.mismatchNote }}
                  </p>
                </div>
              </div>
              <div class="qc-section-gap-row" *ngFor="let sc of sectionGapRows()">
                <div class="qc-section-gap-heading">{{ sectionGapCompetitorHeading(sc) }}</div>
                <div class="qc-section-gap-closest" *ngIf="sectionGapHasCloseMatch(sc)">
                  Closest on your page ({{ sectionGapClosestPct(sc) }}% similar): {{ sectionGapClosestHeading(sc) }}
                </div>
                <div class="qc-section-gap-closest qc-section-gap-none" *ngIf="!sectionGapHasCloseMatch(sc)">
                  Not covered on your page.
                </div>
                <p class="qc-section-gap-terms" *ngIf="sc.absentTerms?.length">
                  Missing terms: {{ sc.absentTerms.join(', ') }}
                </p>
              </div>
            </ng-container>
          </div>
        </div>
      </div>
    </div>

    <ng-template #qcFreshnessBlock let-side="side">
      <div class="qc-metric-card card card--pad qc-freshness-card">
        <div class="metric-label">Freshness</div>
        <div class="qc-freshness-rows">
          <div class="qc-freshness-row">
            <span class="qc-freshness-label">Analyzed:</span>
            <ng-container *ngIf="getAnalyzedAtIsoForSide(side) as analyzedAt; else qcFreshnessNoAnalyzed">
              <span class="qc-freshness-value">{{ analyzedAt | date:'short' }}</span>
            </ng-container>
            <ng-template #qcFreshnessNoAnalyzed>
              <span class="qc-freshness-value">N/A</span>
            </ng-template>
          </div>
          <div class="qc-freshness-row" *ngIf="getLastModifiedIsoForSide(side) as lm">
            <span class="qc-freshness-label">Last modified:</span>
            <span class="qc-freshness-value">{{ lm | date:'short' }}</span>
          </div>
        </div>
      </div>
    </ng-template>

    <ng-template #qcIssuesBlock let-side="side">
      <div class="qc-metric-card card card--pad qc-issues-card">
        <div class="metric-label">Critical issues</div>
        <div class="metric-value" [class.qc-has-issues]="getCriticalIssuesCountForSide(side) > 0">
          {{ getCriticalIssuesCountForSide(side) }}
        </div>
        <div class="qc-dropdown-section" *ngIf="getCriticalIssuesCountForSide(side) > 0">
          <button type="button" class="qc-dropdown-toggle" (click)="toggleIssuesDetails()">
            <span>{{ isIssuesDetailsExpanded() ? 'Hide' : 'Show' }} details</span>
            <span class="qc-dropdown-arrow" [class.expanded]="isIssuesDetailsExpanded()">&#9660;</span>
          </button>
          <div class="qc-dropdown-content" *ngIf="isIssuesDetailsExpanded()">
            <div class="qc-issue-item" *ngFor="let issue of getCriticalIssuesForSide(side)">{{ issue }}</div>
          </div>
        </div>
      </div>
    </ng-template>

    <ng-template #qcTopicFitBlock let-side="side">
      <div class="qc-metric-card card card--pad topic-fit-card" *ngIf="topicSignalsCount(analysisForSide(side)) > 0">
        <div class="metric-label">
          Topic Fit
          <span
            class="info-tooltip"
            title="Shows your domain's authority on this page's main topic. Requires domain crawl and topic aggregation."
            >ⓘ</span>
        </div>
        <ng-container *ngIf="topicFitForSide(side) as topicFit">
          <div class="topic-fit-loading" *ngIf="topicFit.loading">
            <span class="qc-loading-spinner-small"></span>
            Loading…
          </div>
          <div class="topic-fit-error" *ngIf="topicFit.error">
            <span class="error-icon">⚠</span>
            Unable to load
          </div>
          <div class="topic-fit-unavailable" *ngIf="!topicFit.loading && !topicFit.error && !topicFit.hasData">
            <span class="muted-text">Domain topics not available yet</span>
            <span
              class="info-tooltip"
              title="Run a full domain crawl and topic aggregation to see this data."
              >ⓘ</span>
          </div>
          <div
            class="topic-fit-not-found"
            *ngIf="!topicFit.loading && !topicFit.error && topicFit.hasData && !topicFit.topicFound"
          >
            <span class="muted-text">Topic not in domain authority</span>
          </div>
          <div
            class="topic-fit-result"
            *ngIf="!topicFit.loading && !topicFit.error && topicFit.hasData && topicFit.topicFound"
          >
            <span [ngClass]="topicTierBadgeClasses(topicFit.tier)">
              {{ formatTopicTierPublic(topicFit.tier) }}
            </span>
            <span class="topic-fit-details">
              on <strong>{{ topicFit.mainTopicLabel }}</strong>
              <span class="authority-score">(Authority {{ topicFit.authorityScore | number:'1.0-0' }})</span>
            </span>
            <ng-container *ngIf="topicFitSupportingPagesInsight(side) as insight">
              <p class="topic-fit-supporting-insight" *ngIf="insight.show">
                Your site has little depth on <strong>{{ insight.topicLabel }}</strong>. Consider adding supporting pages on this topic (not just improving this URL).
              </p>
            </ng-container>
          </div>
        </ng-container>
      </div>
    </ng-template>

    <!-- Outlinks: promoted to a metric card of its own (§3.4).
         "They link out to more related content (18 vs 2)" was the single most
         actionable fact in this view and existed only as a diagnosis bullet
         further down the page — the number behind it was nowhere.
         It leads the incoming card because it is the half that survives: read
         off this page's own analysis, so it is there for a SERP winner we have
         only ever single-page crawled, where incoming goes to an em dash. -->
    <ng-template #qcOutgoingBlock let-side="side">
      <div class="qc-metric-card card card--pad">
        <div class="metric-label">
          Links out (from this page)
          <span
            class="info-tooltip"
            title="Same-site links this page points to, classified from its own crawl. Available for any page we have fetched."
            >ⓘ</span>
        </div>
        <!-- The TOTAL, not the contextual count, because the card directly
             below is a total and the two are the same shape: a headline 0 over
             a breakdown reading "Structural 86" reads as a broken counter, and
             two adjacent cards whose largest number means different things is
             worse than either choice on its own. Contextual leads the
             breakdown, which is where the comparison actually reads it. -->
        <div class="metric-value" *ngIf="!outgoingLinksUnmeasured(side)">
          {{ getOutgoingLinkStats(side).total }}
        </div>
        <div class="metric-value metric-value--unmeasured" *ngIf="outgoingLinksUnmeasured(side)">—</div>
        <div class="qc-link-warning" *ngIf="outgoingLinksUnmeasured(side)">
          Unavailable — this page has not been crawled.
        </div>
        <div class="qc-link-breakdown" *ngIf="!outgoingLinksUnmeasured(side)">
          <div class="qc-link-stat">
            <span class="qc-link-type-label" [title]="linkTypeTooltip('contextual')">Contextual</span>
            <strong>{{ getOutgoingLinkStats(side).contextual }}</strong>
          </div>
          <div class="qc-link-stat">
            <span class="qc-link-type-label" [title]="linkTypeTooltip('structural')">Structural</span>
            <strong>{{ getOutgoingLinkStats(side).structural }}</strong>
          </div>
          <div class="qc-link-stat">
            <span class="qc-link-type-label" [title]="linkTypeTooltip('same_page')">Same-page</span>
            <strong>{{ getOutgoingLinkStats(side).samePage }}</strong>
          </div>
          <div class="qc-link-stat">
            <span class="qc-link-type-label" [title]="linkTypeTooltip('unknown')">Unknown</span>
            <strong>{{ getOutgoingLinkStats(side).unknown }}</strong>
          </div>
        </div>
      </div>
    </ng-template>

    <ng-template #qcIncomingBlock let-side="side">
      <div class="qc-metric-card card card--pad">
        <div class="metric-label">
          Links in (from your crawled site)
          <span
            class="info-tooltip"
            title="Counts same-site links from your workspace crawl only — not Google backlinks."
            >ⓘ</span>
        </div>
        <p class="qc-link-context-note" *ngIf="incomingFor(side).error && !incomingFor(side).expanded">
          {{ incomingFor(side).error }}
        </p>
        <!-- Em dash, not 0. This card counts links found in OUR crawl of that
             domain, so with no other page of theirs held there is nothing that
             could link here and the 0 is arithmetic, not a measurement. A SERP
             winner is single-page crawled, so this is its normal state -- and a
             0 in the card's largest type would state that the page beating you
             has no internal links. -->
        <div class="metric-value" *ngIf="!incomingLinksUnmeasured(side)">
          {{ getIncomingLinksCount(side) }}
        </div>
        <div class="metric-value metric-value--unmeasured" *ngIf="incomingLinksUnmeasured(side)">—</div>
        <div class="qc-link-warning" *ngIf="incomingLinksUnmeasured(side); else partialCrawlWarning">
          Unavailable — full domain not crawled.
        </div>
        <ng-template #partialCrawlWarning>
          <div class="qc-link-warning" *ngIf="shouldShowIncomingLinksWarning(side)">
            Full domain crawl recommended for complete incoming links data.
          </div>
        </ng-template>
        <div class="qc-link-breakdown" *ngIf="(getIncomingLinksCount(side) ?? 0) > 0">
          <div class="qc-link-stat">
            <span class="qc-link-type-label" [title]="linkTypeTooltip('contextual')">Contextual</span>
            <strong>{{ getIncomingLinksStats(side).contextual }}</strong>
          </div>
          <div class="qc-link-stat">
            <span class="qc-link-type-label" [title]="linkTypeTooltip('structural')">Structural</span>
            <strong>{{ getIncomingLinksStats(side).structural }}</strong>
          </div>
          <div class="qc-link-stat">
            <span class="qc-link-type-label" [title]="linkTypeTooltip('same_page')">Same-page</span>
            <strong>{{ getIncomingLinksStats(side).samePage }}</strong>
          </div>
          <div class="qc-link-stat">
            <span class="qc-link-type-label" [title]="linkTypeTooltip('unknown')">Unknown</span>
            <strong>{{ getIncomingLinksStats(side).unknown }}</strong>
          </div>
        </div>
        <div class="qc-dropdown-section">
          <button
            type="button"
            class="qc-dropdown-toggle"
            (click)="toggleIncomingExpanded(side)"
            [disabled]="incomingExpandDisabled(side)"
          >
            <span *ngIf="!incomingFor(side).fullLoading">
              {{ isIncomingExpanded(side) ? 'Hide' : 'Show' }} Details
            </span>
            <span *ngIf="incomingFor(side).fullLoading">Loading…</span>
            <span class="qc-dropdown-arrow" [class.expanded]="isIncomingExpanded(side)">▼</span>
          </button>
          <div class="qc-dropdown-content" *ngIf="isIncomingExpanded(side)">
            <div class="qc-link-context-note" *ngIf="incomingFor(side).error">
              Error: {{ incomingFor(side).error }}
            </div>
            <div class="qc-link-context-note" *ngIf="!incomingFor(side).error && incomingLinksUnmeasured(side)">
              Unavailable — full domain not crawled. We hold only this page for the domain, so
              nothing of theirs could link to it.
            </div>
            <div
              class="qc-link-context-note"
              *ngIf="!incomingFor(side).error && !incomingLinksUnmeasured(side) && (getIncomingLinksCount(side) ?? 0) === 0"
            >
              No incoming links found from other pages on this domain.
            </div>
            <div class="qc-link-list" *ngIf="!incomingFor(side).error && getDisplayedIncomingLinks(side).length > 0">
              <div class="qc-link-item" *ngFor="let link of getDisplayedIncomingLinks(side)">
                <div class="qc-link-item-header">
                  <span [ngClass]="linkTypeBadgeClasses(link.linkType)" [title]="linkTypeTooltip(link.linkType)">
                    {{ linkTypeLabel(link.linkType) }}
                  </span>
                  <span class="qc-link-meta" *ngIf="link.confidence != null">Confidence: {{ (link.confidence * 100) | number:'1.0-0' }}%</span>
                  <span class="qc-link-meta" *ngIf="link.isBroken">Broken</span>
                  <span class="qc-link-meta" *ngIf="link.dofollow === false">nofollow</span>
                </div>
                <div class="qc-link-item-row">
                  <span class="qc-link-item-label">From:</span>
                  <a class="qc-link-item-value qc-link-anchor" [href]="link.sourceUrl" target="_blank" rel="noopener noreferrer">
                    {{ link.sourcePageTitle || link.sourceUrl }}
                  </a>
                </div>
                <div class="qc-link-item-row">
                  <span class="qc-link-item-label">To:</span>
                  <span class="qc-link-item-value">{{ targetUrlForAnalysis(analysisForSide(side)) }}</span>
                </div>
                <div class="qc-link-item-row">
                  <span class="qc-link-item-label">Anchor:</span>
                  <span class="qc-link-item-value">"{{ anchorText(link.anchorText) }}"</span>
                </div>
              </div>
            </div>
            <button
              type="button"
              class="qc-link-show-more"
              *ngIf="hasMoreIncomingLinks(side) || incomingFor(side).showAll"
              (click)="toggleShowAllIncomingLinks(side)"
            >
              {{ incomingFor(side).showAll ? 'Show less' : 'Show all links' }}
            </button>
            <div
              class="qc-link-context-note"
              *ngIf="!incomingFor(side).error && getDisplayedIncomingLinks(side).length === 0 && (getIncomingLinksCount(side) ?? 0) > 0"
            >
              No contextual or same-page incoming links to show.
            </div>
          </div>
        </div>
      </div>
    </ng-template>

    <footer class="qc-footer">
      <p class="qc-full-compare-hint">
        For SERP overlap, shared entities, and related pages (extra API usage), continue in Full Compare. Nothing
        runs until you choose <strong>Start Comparison</strong> there.
      </p>
      <button
        type="button"
        class="qc-full-compare-btn btn"
        (click)="onOpenFullCompareClick()"
        [disabled]="!canOpenFullCompare"
      >
        Deep compare (Topic Share and related pages)
      </button>
    </footer>
  </div>
</div>

<ng-template #rankedKeywordsNotes let-analysis="analysis">
  <p class="qc-metric-note" *ngIf="rankedKeywordsNoteVisible(analysis)">
    {{ rankedKeywordsNoteForAnalysis(analysis) }}
  </p>
  <p class="qc-metric-note qc-metric-note-dev" *ngIf="rankedKeywordsDevNoteForAnalysis(analysis) as devNote">
    {{ devNote }}
  </p>
</ng-template>
